// src/admin/components/rolePermissions.ts
import React from 'react';
import {
  Dashboard,
  People,
  Groups as CustomersNavIcon,
  AdminPanelSettings as RoleIcon,
  ShoppingCart as ShoppingCartIcon,
  AssignmentReturn as AssignmentReturnIcon,
} from '@mui/icons-material';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import ReviewsIcon from '@mui/icons-material/Reviews';
import AssessmentIcon from '@mui/icons-material/Assessment';
import StoreIcon from '@mui/icons-material/Store';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import AppsIcon from '@mui/icons-material/Apps';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { LocalShipping, Email as EmailIcon } from '@mui/icons-material';
import PaymentsIcon from '@mui/icons-material/Payments';
import LabelIcon from '@mui/icons-material/Label';
import { UserRole } from '../pages/users/userService';


// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
export interface NavItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  badge?: string;
  badgeLoading?: boolean;
  /**
   * The feature code that must be present in userProfile.permissions for this
   * nav item to appear. If undefined the item is always shown.
   * Super-admin bypasses this check entirely — all items are shown.
   */
  permissionCode?: string;
}

export interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Super-admin sentinel — must match the value stored in userClaims.role
// ─────────────────────────────────────────────────────────────────────────────
export const SUPER_ADMIN_ROLE = 'super-admin';

/** Returns true when the role string is 'super-admin' */
export const isSuperAdminRole = (role: string | undefined | null): boolean =>
  role === SUPER_ADMIN_ROLE;

// ─────────────────────────────────────────────────────────────────────────────
// Master nav definition
// Each item carries the feature code that gates it.
// Adding a new page = add one entry here; no role arrays to update.
// ─────────────────────────────────────────────────────────────────────────────
export const ALL_NAV_GROUPS: NavGroup[] = [
  {
    id: 'core',
    label: 'Core',
    items: [
      {
        text: 'Dashboard',
        icon: React.createElement(Dashboard),
        path: '/admin/dashboard',
        permissionCode: 'dashboard.view',
      },
      {
        text: 'User',
        icon: React.createElement(People),
        path: '/admin/users',
        permissionCode: 'users.view',
      },
      {
        text: 'Customers',
        icon: React.createElement(CustomersNavIcon),
        path: '/admin/customers',
        permissionCode: 'customers.view',
      },
      {
        text: 'Roles',
        icon: React.createElement(RoleIcon),
        path: '/admin/roles',
        permissionCode: 'roles.view',
      },
      {
        text: 'Order',
        icon: React.createElement(ShoppingCartIcon),
        path: '/admin/orders',
        permissionCode: 'orders.view',
      },
      {
        text: 'Order Return',
        icon: React.createElement(AssignmentReturnIcon),
        path: '/admin/return',
        permissionCode: 'orders.return.view',
      },
    ],
  },
  {
    id: 'catalog',
    label: 'Catalog',
    items: [
      {
        text: 'Product',
        icon: React.createElement(InventoryIcon),
        path: '/admin/products',
        permissionCode: 'products.view',
      },
      {
        text: 'Category',
        icon: React.createElement(CategoryIcon),
        path: '/admin/category',
        permissionCode: 'category.view',
      },
      {
        text: 'Variant',
        icon: React.createElement(StackedLineChartIcon),
        path: '/admin/variant',
        permissionCode: 'variant.view',
      },
      {
        text: 'Brand',
        icon: React.createElement(AppsIcon),
        path: '/admin/brand',
        permissionCode: 'brand.view',
      },
      {
        text: 'Print Labels',
        icon: React.createElement(LabelIcon),
        path: '/admin/labels',
        permissionCode: 'labels.view',
      },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      {
        text: 'Warehouse',
        icon: React.createElement(WarehouseIcon),
        path: '/admin/warehouse',
        permissionCode: 'warehouse.view',
      },
      {
        text: 'Shipping',
        icon: React.createElement(LocalShipping),
        path: '/admin/shipping',
        permissionCode: 'shipping.view',
      },
      {
        text: 'Purchase Order',
        icon: React.createElement(StoreIcon),
        path: '/admin/purchase-order',
        permissionCode: 'purchase_order.view',
      },
      {
        text: 'PO Return',
        icon: React.createElement(AssignmentReturnIcon),
        path: '/admin/inventory-return',
        permissionCode: 'inventory_return.view',
      },
      {
        text: 'Supplier',
        icon: React.createElement(Diversity3Icon),
        path: '/admin/suplier',
        permissionCode: 'supplier.view',
      },
      {
        text: 'Reports',
        icon: React.createElement(AssessmentIcon),
        path: '/admin/reports',
        permissionCode: 'reports.view',
      },
      {
        text: 'Costs',
        icon: React.createElement(PaymentsIcon),
        path: '/admin/costs',
        permissionCode: 'costs.view',
      },
    ],
  },
  {
    id: 'communication',
    label: 'Communication',
    items: [
      {
        text: 'Messages',
        icon: React.createElement(EmailIcon),
        path: '/admin/messages',
        permissionCode: 'messages.view',
      },
      {
        text: 'Reviews',
        icon: React.createElement(ReviewsIcon),
        path: '/admin/reviews',
        permissionCode: 'reviews.view',
      },
      {
        text: 'Blogs',
        icon: React.createElement(BorderColorIcon),
        path: '/admin/blogs',
        permissionCode: 'blog.view',
      },
      {
        text: 'Newsletter',
        icon: React.createElement(EmailIcon),
        path: '/admin/newsletter',
        permissionCode: 'newsletter.view',
      },
      {
        text: 'Banner',
        icon: React.createElement(WallpaperIcon),
        path: '/admin/banner',
        permissionCode: 'banner.view',
      },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Shared routes — always accessible regardless of permissions
// ─────────────────────────────────────────────────────────────────────────────
export const ALWAYS_ALLOWED_PATHS = [
  '/admin/profile',
  '/admin/settings',
];

// ─────────────────────────────────────────────────────────────────────────────
// getNavGroupsForPermissions
// Returns filtered nav groups based on the live permissions array.
// When superAdmin is true, ALL groups and items are returned without filtering.
// ─────────────────────────────────────────────────────────────────────────────
export const getNavGroupsForPermissions = (
  permissions: string[],
  superAdmin = false
): NavGroup[] => {
  // Super-admin sees every single nav item — no filtering at all
  if (superAdmin) return ALL_NAV_GROUPS;

  const permSet = new Set(permissions);

  return ALL_NAV_GROUPS
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => !item.permissionCode || permSet.has(item.permissionCode)
      ),
    }))
    .filter((group) => group.items.length > 0);
};

// ─────────────────────────────────────────────────────────────────────────────
// isPathAllowedForPermissions
// Returns true if the given path is accessible given the permissions array.
// When superAdmin is true, every path is allowed without checking permissions.
// ─────────────────────────────────────────────────────────────────────────────
export const isPathAllowedForPermissions = (
  permissions: string[],
  path: string,
  superAdmin = false
): boolean => {
  // Super-admin can access every path — return immediately
  if (superAdmin) return true;

  // Paths that are always allowed for any authenticated user
  if (ALWAYS_ALLOWED_PATHS.includes(path)) return true;

  const permSet = new Set(permissions);

  for (const group of ALL_NAV_GROUPS) {
    for (const item of group.items) {
      if (item.path === path) {
        if (!item.permissionCode) return true;
        return permSet.has(item.permissionCode);
      }
    }
  }

  // Unknown path — deny by default for non-super-admin users
  return false;
};

// ─────────────────────────────────────────────────────────────────────────────
// getDefaultPathForPermissions
// Returns the first path the user is allowed to visit — used for redirect
// after login. Super-admin always lands on the dashboard.
// ─────────────────────────────────────────────────────────────────────────────
export const getDefaultPathForPermissions = (
  permissions: string[],
  superAdmin = false
): string => {
  // Super-admin always goes to the dashboard
  if (superAdmin) return '/admin/dashboard';

  const permSet = new Set(permissions);

  for (const group of ALL_NAV_GROUPS) {
    for (const item of group.items) {
      if (!item.permissionCode || permSet.has(item.permissionCode)) {
        return item.path;
      }
    }
  }

  return '/admin/login';
};

// ─────────────────────────────────────────────────────────────────────────────
// Legacy shims — kept so existing imports in App.tsx / AdminLayout.tsx don't
// break during migration. Both delegate to the permission-based functions.
// Remove these once every call-site is updated.
// ─────────────────────────────────────────────────────────────────────────────

/** @deprecated Use getNavGroupsForPermissions instead */
export const getNavGroupsForRole = (role: UserRole | undefined): NavGroup[] => {
  return getNavGroupsForPermissions([]);
};

/** @deprecated Use isPathAllowedForPermissions instead */
export const isPathAllowedForRole = (
  role: UserRole | undefined,
  path: string
): boolean => {
  return ALWAYS_ALLOWED_PATHS.includes(path);
};

/** @deprecated Use getDefaultPathForPermissions instead */
export const getDefaultPathForRole = (role: UserRole | undefined): string => {
  return '/admin/login';
};


// // src/config/rolePermissions.ts
// import React from 'react';
// import {
//   Dashboard,
//   People,
//   AdminPanelSettings as RoleIcon,
//   ShoppingCart as ShoppingCartIcon,
//   AssignmentReturn as AssignmentReturnIcon,
// } from '@mui/icons-material';
// import WallpaperIcon from '@mui/icons-material/Wallpaper';
// import ReviewsIcon from '@mui/icons-material/Reviews';
// import AssessmentIcon from '@mui/icons-material/Assessment';
// import StoreIcon from '@mui/icons-material/Store';
// import Diversity3Icon from '@mui/icons-material/Diversity3';
// import AppsIcon from '@mui/icons-material/Apps';
// import InventoryIcon from '@mui/icons-material/Inventory';
// import CategoryIcon from '@mui/icons-material/Category';
// import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
// import WarehouseIcon from '@mui/icons-material/Warehouse';
// import BorderColorIcon from '@mui/icons-material/BorderColor';
// import { LocalShipping, Email as EmailIcon } from '@mui/icons-material';

// import { UserRole } from '../pages/users/userService';

// export interface NavItem {
//   text: string;
//   icon: React.ReactNode;
//   path: string;
//   badge?: string;
//   badgeLoading?: boolean;
// }

// export interface NavGroup {
//   id: string;
//   label: string;
//   items: NavItem[];
// }

// // ── All possible nav items ────────────────────────────────────────────────────
// const ALL_NAV_GROUPS: NavGroup[] = [
//   {
//     id: 'core',
//     label: 'Core',
//     items: [
//       { text: 'Dashboard',    icon: React.createElement(Dashboard),           path: '/admin/dashboard' },
//       { text: 'User',         icon: React.createElement(People),              path: '/admin/users' },
//       { text: 'Roles',         icon: React.createElement(RoleIcon),              path: '/admin/roles' },
//       { text: 'Order',        icon: React.createElement(ShoppingCartIcon),    path: '/admin/orders' },
//       { text: 'Order Return', icon: React.createElement(AssignmentReturnIcon),path: '/admin/return' },
//     ],
//   },
//   {
//     id: 'catalog',
//     label: 'Catalog',
//     items: [
//       { text: 'Product',  icon: React.createElement(InventoryIcon),        path: '/admin/products' },
//       { text: 'Category', icon: React.createElement(CategoryIcon),         path: '/admin/category' },
//       { text: 'Variant',  icon: React.createElement(StackedLineChartIcon), path: '/admin/variant' },
//       { text: 'Brand',    icon: React.createElement(AppsIcon),             path: '/admin/brand' },
//     ],
//   },
//   {
//     id: 'operations',
//     label: 'Operations',
//     items: [
//       { text: 'Warehouse',      icon: React.createElement(WarehouseIcon),         path: '/admin/warehouse' },
//       { text: 'Shipping',       icon: React.createElement(LocalShipping),         path: '/admin/shipping' },
//       { text: 'Purchase Order', icon: React.createElement(StoreIcon),             path: '/admin/purchase-order' },
//       { text: 'PO Return',      icon: React.createElement(AssignmentReturnIcon),  path: '/admin/inventory-return' },
//       { text: 'Supplier',       icon: React.createElement(Diversity3Icon),        path: '/admin/suplier' },
//       { text: 'Reports',        icon: React.createElement(AssessmentIcon),        path: '/admin/reports' },
//     ],
//   },
//   {
//     id: 'communication',
//     label: 'Communication',
//     items: [
//       { text: 'Messages',   icon: React.createElement(EmailIcon),       path: '/admin/messages' },
//       { text: 'Reviews',    icon: React.createElement(ReviewsIcon),     path: '/admin/reviews' },
//       { text: 'Blogs',      icon: React.createElement(BorderColorIcon), path: '/admin/blogs' },
//       { text: 'Newsletter', icon: React.createElement(EmailIcon),       path: '/admin/newsletter' },
//       { text: 'Banner', icon: React.createElement(WallpaperIcon),       path: '/admin/banner' },
//     ],
//   },
// ];

// // ── Allowed paths per role ────────────────────────────────────────────────────
// // Each role lists every path it is permitted to access.
// export const ROLE_ALLOWED_PATHS: Record<UserRole, string[]> = {
//   // Regular users have no admin access at all — they should never reach AdminLayout.
//   // This entry is a safety net only.
//   user: [],

//   // Staff: read-only / operational tasks — no user mgmt, no financials
//   staff: [
//     '/admin/dashboard',
//     '/admin/orders',
//     '/admin/return',
//     '/admin/products',
//     '/admin/category',
//     '/admin/variant',
//     '/admin/brand',
//     '/admin/warehouse',
//     '/admin/shipping',
//     '/admin/messages',
//     '/admin/reviews',
//     '/admin/banner',
//   ],

//   // Manager: everything staff can do + purchasing / suppliers / reports / blogs
//   manager: [
//     '/admin/dashboard',
//     '/admin/orders',
//     '/admin/return',
//     '/admin/products',
//     '/admin/category',
//     '/admin/variant',
//     '/admin/brand',
//     '/admin/warehouse',
//     '/admin/shipping',
//     '/admin/purchase-order',
//     '/admin/inventory-return',
//     '/admin/suplier',
//     '/admin/reports',
//     '/admin/messages',
//     '/admin/reviews',
//     '/admin/blogs',
//     '/admin/newsletter',
//     '/admin/banner',
//   ],

//   // Admin: full access
//   admin: [
//     '/admin/dashboard',
//     '/admin/users',
//     '/admin/orders',
//     '/admin/return',
//     '/admin/products',
//     '/admin/category',
//     '/admin/variant',
//     '/admin/brand',
//     '/admin/warehouse',
//     '/admin/shipping',
//     '/admin/purchase-order',
//     '/admin/inventory-return',
//     '/admin/suplier',
//     '/admin/reports',
//     '/admin/messages',
//     '/admin/reviews',
//     '/admin/blogs',
//     '/admin/newsletter',
//     '/admin/banner',
//     '/admin/roles',
//   ],
// };

// // ── Shared routes always accessible regardless of role (profile, settings) ───
// export const ALWAYS_ALLOWED_PATHS = [
//   '/admin/profile',
//   '/admin/settings',
// ];

// /**
//  * Returns the filtered nav groups for a given role.
//  * Groups or items with no allowed paths are omitted entirely.
//  */
// export const getNavGroupsForRole = (role: UserRole | undefined): NavGroup[] => {
//   const effectiveRole: UserRole = role ?? 'user';
//   const allowed = new Set([
//     ...ROLE_ALLOWED_PATHS[effectiveRole],
//     ...ALWAYS_ALLOWED_PATHS,
//   ]);

//   return ALL_NAV_GROUPS
//     .map(group => ({
//       ...group,
//       items: group.items.filter(item => allowed.has(item.path)),
//     }))
//     .filter(group => group.items.length > 0);
// };

// /**
//  * Returns true if the given role is allowed to access the given path.
//  */
// export const isPathAllowedForRole = (
//   role: UserRole | undefined,
//   path: string
// ): boolean => {
//   const effectiveRole: UserRole = role ?? 'user';
//   const allowed = new Set([
//     ...ROLE_ALLOWED_PATHS[effectiveRole],
//     ...ALWAYS_ALLOWED_PATHS,
//   ]);
//   return allowed.has(path);
// };

// /**
//  * Returns the first allowed path for a role (used as the default redirect).
//  */
// export const getDefaultPathForRole = (role: UserRole | undefined): string => {
//   const effectiveRole: UserRole = role ?? 'user';
//   const paths = ROLE_ALLOWED_PATHS[effectiveRole];
//   return paths.length > 0 ? paths[0] : '/admin/login';
// };